import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Activity,
    AlertTriangle,
    CheckCircle2,
    Cpu,
    Database,
    Gauge,
    HardDriveDownload,
    ListTree,
    MemoryStick,
    Sparkles,
    Webhook,
    Wifi,
    WifiOff,
} from "lucide-react";
import { getBackendUrl as resolveBackendUrl } from "@/lib/runtimeConfig";

const STATUS_COLOURS = {
    operational: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
    degraded: "text-amber-400 bg-amber-400/10 border-amber-400/30",
    down: "text-rose-400 bg-rose-400/10 border-rose-400/30",
    unknown: "text-white/40 bg-white/5 border-white/10",
    yes: "text-emerald-400",
    stale: "text-amber-400",
    no: "text-rose-400",
};

const PROVIDER_LABEL = { anthropic: "Anthropic", openai: "OpenAI", gemini: "Gemini" };

function formatUptime(seconds) {
    if (!seconds && seconds !== 0) return "—";
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

function StatusDot({ value }) {
    const cls = STATUS_COLOURS[value] || STATUS_COLOURS.unknown;
    return <span className={`inline-flex w-2 h-2 rounded-full ${cls.split(" ")[0]} bg-current`} />;
}

function StatChip({ icon: Icon, label, value, status, subtitle }) {
    const color = STATUS_COLOURS[status] || STATUS_COLOURS.unknown;
    return (
        <div
            className={`rounded-lg border ${color} px-4 py-3 transition-all`}
            data-testid={`status-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/55">
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {label}
                </div>
                <StatusDot value={status} />
            </div>
            <div className="mt-2 font-display text-2xl tabular-nums leading-none">
                {value ?? "—"}
            </div>
            {subtitle && <div className="text-[11px] text-white/40 font-mono mt-1">{subtitle}</div>}
        </div>
    );
}

function ProviderRow({ name, info }) {
    const reachable = info?.reachable || "unknown";
    const cls = STATUS_COLOURS[reachable] || STATUS_COLOURS.unknown;
    const label =
        reachable === "yes" ? "Healthy"
            : reachable === "stale" ? "Stale"
                : reachable === "no" ? "Unreachable"
                    : "Unknown";
    const seconds = info?.seconds_since;
    const since = seconds == null
        ? "Awaiting first call"
        : seconds < 60 ? `${seconds}s ago`
            : seconds < 3600 ? `${Math.floor(seconds / 60)}m ago`
                : `${Math.floor(seconds / 3600)}h ago`;
    return (
        <div
            className="flex items-center justify-between py-1.5"
            data-testid={`status-llm-${name}`}
        >
            <div className="flex items-center gap-2 text-sm text-white/80">
                <span className={`w-1.5 h-1.5 rounded-full bg-current ${cls.split(" ")[0]}`} />
                {PROVIDER_LABEL[name] || name}
            </div>
            <div className="text-[11px] font-mono text-white/40">
                <span className={cls.split(" ")[0]}>{label}</span>
                <span className="mx-2 text-white/20">·</span>
                {since}
            </div>
        </div>
    );
}

/** Public system-status widget. Pulls a snapshot via REST as a
 *  cold-start fallback, then upgrades to a WebSocket subscription
 *  for live updates every 5 seconds. */
export function SystemStatusPanel() {
    const [snap, setSnap] = useState(null);
    const [wsState, setWsState] = useState("connecting"); // connecting | live | offline
    const [error, setError] = useState("");
    const wsRef = useRef(null);
    const reconnectRef = useRef({ tries: 0, timer: null });

    const apiBase = resolveBackendUrl();

    // 1) REST fallback: fetch immediately so the panel never shows blank
    useEffect(() => {
        let cancelled = false;
        fetch(`${apiBase}/api/v1/status`)
            .then((r) => r.json())
            .then((data) => {
                if (!cancelled) setSnap((cur) => cur || data);
            })
            .catch((e) => !cancelled && setError(e.message));
        return () => {
            cancelled = true;
        };
    }, [apiBase]);

    // 2) WebSocket: live updates
    useEffect(() => {
        let manualClose = false;
        const reconnect = reconnectRef.current;
        const connect = () => {
            const wsUrl =
                (apiBase || window.location.origin).replace(/^http/, "ws") + "/api/v1/status/ws";
            try {
                const ws = new WebSocket(wsUrl);
                wsRef.current = ws;
                setWsState("connecting");
                ws.onopen = () => {
                    setWsState("live");
                    reconnectRef.current.tries = 0;
                };
                ws.onmessage = (ev) => {
                    try {
                        const data = JSON.parse(ev.data);
                        setSnap(data);
                    } catch {
                        // ignore malformed payload
                    }
                };
                ws.onclose = () => {
                    if (manualClose) return;
                    setWsState("offline");
                    const tries = ++reconnectRef.current.tries;
                    const delayMs = Math.min(30_000, 1_000 * 2 ** Math.min(tries, 5));
                    reconnectRef.current.timer = setTimeout(connect, delayMs);
                };
                ws.onerror = () => {
                    setWsState("offline");
                };
            } catch (e) {
                setError(String(e));
                setWsState("offline");
            }
        };
        connect();
        return () => {
            manualClose = true;
            if (reconnect.timer) clearTimeout(reconnect.timer);
            try { wsRef.current?.close(); } catch {
                /* swallow */
            }
        };
    }, [apiBase]);

    const overall = snap?.status || "unknown";
    const overallLabel = useMemo(() => ({
        operational: "All systems operational",
        degraded: "Partial degradation",
        down: "Service disruption",
        unknown: "Awaiting data",
    }[overall]), [overall]);
    const OverallIcon = overall === "operational"
        ? CheckCircle2
        : overall === "degraded"
            ? AlertTriangle
            : overall === "down"
                ? AlertTriangle
                : Activity;

    return (
        <div
            className="w-full max-w-[480px] mx-auto px-2 py-6"
            data-testid="system-status-panel"
        >
            {/* Brand */}
            <div className="flex items-center gap-3 mb-10">
                <div className="grid grid-cols-1 gap-1">
                    <div className="flex gap-0.5">
                        <span className="w-2 h-2 rounded-sm bg-[#7C3AED]" />
                        <span className="w-2 h-2 rounded-sm bg-[#7C3AED]" />
                    </div>
                    <div className="flex gap-0.5">
                        <span className="w-2 h-2 rounded-sm bg-[#7C3AED]" />
                        <span className="w-2 h-2 rounded-sm bg-[#7C3AED]" />
                    </div>
                </div>
                <div>
                    <div className="font-display text-lg font-semibold tracking-tight">
                        <span className="text-white">AI</span>
                        <span className="text-[#7C3AED]">Service</span>
                    </div>
                    <div className="label-xs text-white/40 mt-0.5">Live system status</div>
                </div>
                <div className="ml-auto flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider"
                    data-testid="status-ws-state">
                    {wsState === "live"
                        ? <><Wifi className="w-3 h-3 text-emerald-400" /> <span className="text-emerald-400">Live</span></>
                        : wsState === "connecting"
                            ? <><Wifi className="w-3 h-3 text-amber-400 animate-pulse" /> <span className="text-amber-400">Connecting</span></>
                            : <><WifiOff className="w-3 h-3 text-white/30" /> <span className="text-white/30">Offline</span></>}
                </div>
            </div>

            {/* Headline status */}
            <div
                className={`rounded-xl border p-5 mb-5 ${STATUS_COLOURS[overall] || STATUS_COLOURS.unknown}`}
                data-testid="status-headline"
            >
                <div className="flex items-center gap-3">
                    <OverallIcon className="w-5 h-5" strokeWidth={1.5} />
                    <div className="font-display text-xl tracking-tight">{overallLabel}</div>
                </div>
                <div className="mt-2 text-[11px] text-white/45 font-mono flex flex-wrap gap-x-4 gap-y-1">
                    {snap?.version && <span>version <span className="text-white/85" data-testid="status-version">v{snap.version}</span></span>}
                    <span>uptime <span className="text-white/85" data-testid="status-uptime">{formatUptime(snap?.uptime_seconds)}</span></span>
                    {snap?.timestamp && (
                        <span>updated <span className="text-white/85">{new Date(snap.timestamp).toLocaleTimeString()}</span></span>
                    )}
                </div>
            </div>

            {/* 4-up grid */}
            <div className="grid grid-cols-2 gap-3 mb-5">
                <StatChip
                    icon={Gauge}
                    label="API p95"
                    value={snap?.metrics ? `${snap.metrics.p95_ms} ms` : null}
                    subtitle={snap?.metrics ? `p50 ${snap.metrics.p50_ms} ms` : null}
                    status={snap?.components?.api?.status}
                />
                <StatChip
                    icon={Activity}
                    label="Throughput"
                    value={snap?.metrics ? `${snap.metrics.requests_per_minute} rpm` : null}
                    subtitle={snap?.metrics ? `${snap.metrics.samples_5m} samples / 5m` : null}
                    status={snap?.components?.api?.status}
                />
                <StatChip
                    icon={Database}
                    label="MongoDB"
                    value={snap?.components?.database?.latency_ms != null
                        ? `${snap.components.database.latency_ms} ms`
                        : null}
                    subtitle="ping"
                    status={snap?.components?.database?.status}
                />
                <StatChip
                    icon={Webhook}
                    label="Webhooks"
                    value={snap?.components?.webhook_queue?.depth != null
                        ? `${snap.components.webhook_queue.depth}`
                        : null}
                    subtitle={snap?.components?.webhook_queue
                        ? `${snap.components.webhook_queue.dead || 0} dead`
                        : null}
                    status={snap?.components?.webhook_queue?.status}
                />
            </div>

            {/* Phase U — task queue + resource gauges */}
            <div className="grid grid-cols-3 gap-3 mb-5">
                <StatChip
                    icon={ListTree}
                    label="Task queue"
                    value={snap?.components?.task_manager?.queued != null
                        ? `${snap.components.task_manager.queued}`
                        : null}
                    subtitle={snap?.components?.task_manager
                        ? `${snap.components.task_manager.running || 0} running · ${snap.components.task_manager.dead || 0} dead`
                        : null}
                    status={snap?.components?.task_manager?.status}
                />
                <StatChip
                    icon={Cpu}
                    label="CPU"
                    value={snap?.components?.task_manager?.cpu_pct != null
                        ? `${snap.components.task_manager.cpu_pct}%`
                        : null}
                    subtitle={snap?.components?.task_manager?.paused_by_resource
                        ? "throttled"
                        : "headroom ok"}
                    status={
                        snap?.components?.task_manager?.cpu_pct > 80
                            ? "down"
                            : snap?.components?.task_manager?.cpu_pct > 70
                                ? "degraded"
                                : "operational"
                    }
                />
                <StatChip
                    icon={MemoryStick}
                    label="Memory"
                    value={snap?.components?.task_manager?.ram_pct != null
                        ? `${snap.components.task_manager.ram_pct}%`
                        : null}
                    subtitle={snap?.components?.task_manager
                        ? `${snap.components.task_manager.global_concurrency || 32} slots`
                        : null}
                    status={
                        snap?.components?.task_manager?.ram_pct > 80
                            ? "down"
                            : snap?.components?.task_manager?.ram_pct > 70
                                ? "degraded"
                                : "operational"
                    }
                />
            </div>

            {/* LLM providers */}
            <div
                className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
                data-testid="status-llm-section"
            >
                <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-3.5 h-3.5 text-[#a17bff]" strokeWidth={1.5} />
                    <span className="label-xs text-white/55">LLM providers</span>
                </div>
                <div className="divide-y divide-white/5">
                    {Object.entries(snap?.llm_providers || {}).map(([name, info]) => (
                        <ProviderRow key={name} name={name} info={info} />
                    ))}
                    {(!snap?.llm_providers || !Object.keys(snap.llm_providers).length) && (
                        <div className="text-xs text-white/30 py-2">Awaiting first AI call…</div>
                    )}
                </div>
            </div>

            {error && (
                <div className="text-[10px] text-rose-400/70 font-mono mt-4">
                    Status feed error: {error}
                </div>
            )}

            <div className="mt-6 text-[10px] text-white/25 font-mono flex items-center gap-1.5">
                <HardDriveDownload className="w-3 h-3" strokeWidth={1.5} />
                Machine-readable: <code className="text-white/40">/api/v1/status</code> · Prometheus:{" "}
                <code className="text-white/40">/api/v1/status/metrics</code>
            </div>
        </div>
    );
}

export default SystemStatusPanel;
